import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Phone, Mail, MapPin, User, Building2, Calendar, Printer, Loader2 } from 'lucide-react';
import { useClients, useDeleteClient } from '../../hooks/useClients';
import { useDebounce } from '../../hooks/useDebounce';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { formatDateShort } from '../../lib/utils';
import { toast } from 'sonner';
import { exportarClientesPDF } from '../../api/clients';

const ITEMS_PER_PAGE = 20;

export function ClientList() {
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 300);

  const { data: paginationData, isLoading, isError, refetch } = useClients({
    search: debouncedSearch.trim() || undefined,
    page,
    limit: ITEMS_PER_PAGE,
  });

  const deleteClient = useDeleteClient();

  const clients = paginationData?.data || [];
  const totalItems = paginationData?.total || 0;
  const totalPages = paginationData?.totalPages || 1;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const calcularDias = (fechaRegistro: string) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const registro = new Date(fechaRegistro);
    registro.setHours(0, 0, 0, 0);

    const diffTime = hoy.getTime() - registro.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getAniversarioTexto = (dias: number) => {
    if (dias === 0) return ' ¡Hoy es su día!';
    if (dias === 1) return ' 1 día como cliente';
    return ` ${dias} días como cliente`;
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este cliente?')) return;

    try {
      await deleteClient.mutateAsync(id);
      toast.success('Cliente eliminado exitosamente');
      refetch();
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Error al eliminar el cliente');
      }
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsExporting(true);
      toast.info('Generando PDF de clientes...');

      const pdfUrl = await exportarClientesPDF(search);

      window.open(pdfUrl, '_blank');
      toast.success('PDF generado exitosamente');
    } catch (error: any) {
      if (error.response?.status === 404) {
        toast.warning('No hay clientes para exportar');
      } else {
        toast.error('Error al generar el PDF');
        console.error(error);
      }
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando clientes...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar los clientes
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Clientes</h1>
          <p className="text-sm text-gray-500">
            {totalItems} cliente(s) registrados
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={isExporting || totalItems === 0}
            className="border-blue-200 text-blue-600 hover:bg-blue-50"
          >
            {isExporting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Printer size={16} />
            )}
            {isExporting ? 'Generando...' : 'Exportar PDF'}
          </Button>

          <Link to="/clientes/nuevo">
            <Button>
              <Plus size={18} />
              Nuevo Cliente
            </Button>
          </Link>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          type="text"
          placeholder="Buscar clientes por nombre, CUIL, teléfono o localidad..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients.map((client) => {
          const dias = calcularDias(client.fechaRegistro);

          return (
            <div key={client.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                      <User className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{client.nombre}</h3>
                      {client.cuil && (
                        <p className="text-xs text-gray-400">CUIL: {client.cuil}</p>
                      )}
                      <p className={`text-xs font-medium mt-0.5 ${dias === 0 ? 'text-green-600' : 'text-blue-600'}`}>
                        <Calendar size={12} className="inline mr-1" />
                        {getAniversarioTexto(dias)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Link
                    to={`/clientes/editar/${client.id}`}
                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </Link>
                  <button
                    onClick={() => handleDelete(client.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="mt-3 space-y-1.5 text-sm">
                {client.telefono && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone size={14} className="text-gray-400" />
                    <span>{client.telefono}</span>
                  </div>
                )}
                {client.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail size={14} className="text-gray-400" />
                    <span className="truncate">{client.email}</span>
                  </div>
                )}
                {client.direccion && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={14} className="text-gray-400" />
                    <span className="truncate">{client.direccion}</span>
                  </div>
                )}
                {client.localidad && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building2 size={14} className="text-gray-400" />
                    <span>{client.localidad}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                Registrado: {formatDateShort(client.fechaRegistro)}
              </div>
            </div>
          );
        })}
      </div>

      {clients.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p>No se encontraron clientes</p>
        </div>
      )}

      {totalItems > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}